import { ComponentProps, ReactNode } from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "../../utils/class-names";

export const buttonV2 = tv({
  base: "text-base font-sans font-medium text-white rounded-lg px-2.5 py-2.5 hover:cursor-pointer hover:transition hover:ease-in hover:duration-150",
  variants: {
    variant: {
      primary: "bg-blue-500 hover:bg-blue-400 active:bg-blue-600",
      secondary: "bg-shale-800 hover:bg-shale-750 active:bg-shale-800",
      tertiary: "bg-shale-600 hover:bg-shale-500 active:bg-shale-700",
      action: cn(
        "border-shale-700 border lg:border-0",
        "text-sm lg:text-base",
        "px-5 py-2.5 lg:px-0 lg:py-0",
        "text-blue-300 hover:bg-shale-750 lg:hover:bg-transparent hover:text-blue-200 active:bg-shale-850 lg:active:bg-transparent"
      ),
    },
    disabled: {
      true: "pointer-events-none text-shale-400",
    },
    isSelected: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: "primary",
      disabled: true,
      className: "bg-shale-750",
    },
    {
      variant: "secondary",
      disabled: true,
      className: "bg-shale-800",
    },
    {
      variant: "secondary",
      isSelected: true,
      className: "bg-shale-700 hover:bg-shale-600 active:bg-shale-700",
    },
    {
      variant: "secondary",
      isSelected: false,
      className: "font-normal text-shale-200",
    },
    {
      variant: "tertiary",
      disabled: true,
      className: "bg-shale-700",
    },
    {
      variant: "action",
      isSelected: true,
      className: "text-white bg-shale-700 lg:bg-transparent",
    },
  ],
});

type ButtonVariantProps = VariantProps<typeof buttonV2>;

export type ButtonVariantV2 = NonNullable<ButtonVariantProps["variant"]>;

type ButtonType = ComponentProps<"button">["type"];

export interface ButtonV2Props {
  className?: string;
  children?: ReactNode;
  variant: ButtonVariantV2;
  type?: ButtonType;
  onClick?: () => void;
  disabled?: boolean;
  isSelected?: boolean;
  ["data-cy"]?: string;
}

export function ButtonV2({
  className,
  children,
  variant,
  type = "button",
  disabled = false,
  onClick,
  isSelected,
  "data-cy": dataCy,
}: ButtonV2Props): JSX.Element {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      className={buttonV2({ variant, disabled, className, isSelected })}
      aria-disabled={disabled}
      disabled={disabled}
      data-cy={dataCy}
      type={type}
    >
      {children}
    </button>
  );
}
